import { type ReactNode, useEffect, useRef } from 'react';

import { Separator } from '@nesso/ui/primitives/separator';

import type { NessoContent, UpdateSessionNoteContentFn } from '@/entities/session-note';

import { NessoAdditionalNotes } from './nesso-session-note-content/nesso-additional-notes';
import { NessoChiefComplaint } from './nesso-session-note-content/nesso-chief-complaint';
import { NessoClientPresentation } from './nesso-session-note-content/nesso-client-presentation';
import { NessoInterventions } from './nesso-session-note-content/nesso-interventions';
import { NessoPlanSection } from './nesso-session-note-content/nesso-plan-section';
import { NessoProgressSection } from './nesso-session-note-content/nesso-progress-section';
import { NessoRiskSection } from './nesso-session-note-content/nesso-risk-section';

type Props = {
    content: NessoContent;
    updateContent: UpdateSessionNoteContentFn;
    disclaimer?: ReactNode;
};

export function NessoSessionNoteContent({ content, updateContent, disclaimer }: Props) {
    const contentRef = useRef(content);
    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    function update<K extends keyof NessoContent>(key: K, value: NessoContent[K]) {
        updateContent('NESSO', { ...contentRef.current, [key]: value });
    }

    return (
        <div className="space-y-8">
            <div className="flex flex-col gap-11">
                <NessoChiefComplaint
                    content={content.chiefComplaint}
                    onChange={(next) => update('chiefComplaint', next)}
                />
                <Separator />
                <NessoClientPresentation
                    content={content.clientPresentation}
                    onChange={(next) => update('clientPresentation', next)}
                />
                <Separator />
                <NessoRiskSection
                    content={content.risk ?? undefined}
                    onChange={(next) => update('risk', next)}
                />
                <Separator />
                <NessoInterventions
                    content={content.interventions}
                    onChange={(next) => update('interventions', next)}
                />
                <Separator />
                <NessoProgressSection
                    content={content.progress}
                    onChange={(next) => update('progress', next)}
                />
                <Separator />
                <NessoPlanSection
                    content={content.plan}
                    onChange={(next) => update('plan', next)}
                />
                <Separator />
                <NessoAdditionalNotes
                    value={content.additionalNotes ?? ''}
                    onChange={(next) => update('additionalNotes', next)}
                />
            </div>
            {disclaimer}
        </div>
    );
}
